import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChat } from '../context/ChatContext';
import { chatUsers, chatPresence, uploadChatFiles, mediaUrl, listMessages, sendMessage, addGroupMember, removeGroupMember, renameGroup, reactMessage, recallMessage, deleteMessageForMe, remindMessage, muteConversation, listObserverGroups, pinMessage, unpinMessage, pinConversation, REACTION_EMOJIS } from '../api/chat';
import { fmtSize } from '../utils/chatFiles';
import { createNote } from '../api/notes';
import { getSocket, emitTyping } from '../socket';
import { useToast } from './Toast';
import EmojiPicker from './EmojiPicker';
import { CustomerConversationList, CustomerChatWindow } from './CustomerChat';

// Tin chỉ gồm emoji (ngắn) -> render cỡ lớn như sticker.
const EMOJI_ONLY = /^(?:[\p{Extended_Pictographic}‍️⃣\u{1F3FB}-\u{1F3FF}]|\s)+$/u;
function isBigEmoji(t) {
  if (!t) return false;
  const s = t.trim();
  if (!s || !EMOJI_ONLY.test(s)) return false;
  return [...s].filter((c) => !/\s/.test(c)).length <= 6;
}

// Hiển thị 1 đính kèm trong bong bóng tin nhắn (ảnh xem trực tiếp, tệp tải về).
function AttachmentView({ a }) {
  const src = mediaUrl(a.url || a.data);
  if ((a.type || '').startsWith('image/')) {
    // Bấm vào ảnh -> mở lightbox in-app (xử lý bằng delegation ở .chatmsgs), không mở tab mới.
    return <img className="chat-att-img" src={src} alt={a.name} title="Bấm để xem to" style={{ cursor: 'zoom-in' }} />;
  }
  return (
    <a className="chat-att-file" href={src} download={a.name} target="_blank" rel="noreferrer">
      📎 <span className="chat-att-name">{a.name}</span>
      <span className="chat-att-size">{fmtSize(a.size)}</span>
    </a>
  );
}

const initials = (name) => (name || '?').trim().split(/\s+/).slice(-1)[0]?.[0]?.toUpperCase() || '?';
// Ruột khung avatar: có ảnh đại diện -> ảnh tròn phủ kín khung .chat-avatar; không -> chữ cái đầu.
const AvaImg = ({ src, name }) => (src ? <img className="chat-avatar-img" src={src} alt="" /> : initials(name));
const fmtTime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const today = new Date();
  const sameDay = dt.toDateString() === today.toDateString();
  if (sameDay) return dt.toLocaleTimeString('vi', { hour: '2-digit', minute: '2-digit' });
  const p = (n) => String(n).padStart(2, '0');
  return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()}`;
};
// Giờ DƯỚI TIN NHẮN: hôm nay chỉ HH:MM; tin NGÀY CŨ kèm cả ngày tháng (năm chỉ hiện khi khác năm).
// Tách khỏi fmtTime vì fmtTime còn dùng ở DANH SÁCH hội thoại (cột hẹp, giờ+ngày sẽ tràn).
const fmtMsgTime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const now = new Date();
  const hm = dt.toLocaleTimeString('vi', { hour: '2-digit', minute: '2-digit' });
  if (dt.toDateString() === now.toDateString()) return hm;
  const p = (n) => String(n).padStart(2, '0');
  const dm = `${p(dt.getDate())}/${p(dt.getMonth() + 1)}`;
  return dt.getFullYear() === now.getFullYear() ? `${hm} · ${dm}` : `${hm} · ${dm}/${dt.getFullYear()}`;
};

export default function ChatWidget() {
  const chat = useChat();
  const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);
  useEffect(() => {
    const h = () => setVw(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  if (!chat || !chat.me) return null;
  const { open, setOpen, unreadConvs, windows, view, openConv } = chat;

  // Hiện TỐI ĐA số cửa sổ hội thoại còn VỪA bề ngang (không đè nhau; ưu tiên cửa sổ mới nhất).
  // ≤980px: chế độ full màn (CSS lo), chỉ cần 1 cửa sổ.
  const GAP = 12; // khoảng cách giữa các cửa sổ (khớp .chatdock gap)
  // Bề rộng cửa sổ = kích thước user tự chỉnh (nếu có), mặc định 360.
  const savedW = (() => {
    try { const s = JSON.parse(localStorage.getItem('ft_chat_size') || 'null'); return s && s.w ? s.w : null; } catch { return null; }
  })();
  const convW = (savedW || 360) + GAP;
  const listW = 340 + GAP; // cột danh sách tin nhắn (cố định bên phải)
  const MARGIN = 40; // lề an toàn trái — cửa sổ ĐƯỢC PHÉP che sidebar (màn hẹp quý từng px;
  // trước đây chừa hẳn 250px cho sidebar nên laptop 1366px chỉ mở nổi 1 cửa sổ)
  const fit =
    vw <= 980 ? 1 : Math.max(1, Math.floor((vw - 22 - listW - MARGIN) / convW)); // trừ lề phải, cột danh sách, lề trái
  const visibleWindows = windows.slice(-fit);
  // Hội thoại đang mở nhưng KHÔNG đủ chỗ hiện -> thu thành chip tròn xếp dọc cạnh trái dock;
  // bấm chip là đưa lên trước (openConv tự đẩy về cuối mảng = vị trí hiển thị).
  const hiddenWindows = vw <= 980 ? [] : windows.slice(0, -fit);

  return (
    <>
      <ChatToasts />
      {!open && (
        <button className="chatfab" onClick={() => setOpen(true)} title="Tin nhắn">
          💬
          {unreadConvs > 0 && <span className="chatfab-badge">{unreadConvs > 99 ? '99+' : unreadConvs}</span>}
        </button>
      )}
      {open && (
        <div className="chatdock">
          {/* Chip các hội thoại mở nhưng hết chỗ hiện — bấm để đưa lên trước */}
          {hiddenWindows.length > 0 && (
            <div className="chatdock-hidden">
              {hiddenWindows.map((c) => (
                <button key={c.id} className="chatdock-chip" title={c.name || 'Hội thoại'} onClick={() => openConv(c)}>
                  {c.type === 'group' ? '👥' : <AvaImg src={c.other?.avatar} name={c.name} />}
                </button>
              ))}
            </div>
          )}
          {/* Các cửa sổ hội thoại mở ra bên trái cột quản lý (chỉ hiện số vừa màn hình).
              kind='customer' -> cửa sổ chat KHÁCH HÀNG (bidnhanh), còn lại là chat nội bộ. */}
          {visibleWindows.map((c, i) =>
            c.kind === 'customer' ? (
              <CustomerChatWindow key={c.id} conv={c} front={i === visibleWindows.length - 1} />
            ) : (
              <ChatWindow key={c.id} conv={c} front={i === visibleWindows.length - 1} />
            )
          )}
          {/* Cột quản lý tin nhắn — cố định ngoài cùng bên phải */}
          <div className="chatpanel chatpanel-list">
            <ListHeader />
            {view === 'list' && <ChatTabs />}
            <div className="chatbody">
              {view === 'new' ? <NewChat /> : view === 'settings' ? <Settings /> : view === 'presence' ? <PresencePanel /> : chat.tab === 'customer' ? <CustomerConversationList /> : <ConversationList />}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ChatToasts() {
  const { toasts, dismissToast, openConvById } = useChat();
  if (!toasts?.length) return null;
  return (
    <div className="chat-toasts">
      {toasts.map((t) => (
        <div
          key={t.id}
          className="chat-toast"
          onClick={() => {
            openConvById(t.convId, t.kind); // kind='customer' -> mở bên tab Khách hàng
            dismissToast(t.id);
          }}
        >
          <div className="chat-toast-ic">💬</div>
          <div className="chat-toast-main">
            <div className="chat-toast-title">{t.title}</div>
            <div className="chat-toast-body">{t.body}</div>
          </div>
          <button
            className="chat-toast-x"
            onClick={(e) => {
              e.stopPropagation();
              dismissToast(t.id);
            }}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

function ListHeader() {
  const { view, setView, setOpen, tab } = useChat();
  const title = view === 'new' ? 'Cuộc trò chuyện mới' : view === 'settings' ? 'Cài đặt thông báo' : view === 'presence' ? 'Trạng thái nhân viên' : 'Tin nhắn';
  return (
    <div className="chathead">
      {view !== 'list' && (
        <button className="chathead-btn" onClick={() => setView('list')} title="Quay lại danh sách">
          ‹
        </button>
      )}
      <div className="chathead-title">{title}</div>
      {view === 'list' && (
        <>
          {tab !== 'customer' && (
            <button className="chathead-btn" onClick={() => setView('presence')} title="Ai đang online / offline bao lâu">
              👥
            </button>
          )}
          <button className="chathead-btn" onClick={() => setView('settings')} title="Cài đặt thông báo">
            ⚙
          </button>
          {tab !== 'customer' && (
            <button className="chathead-btn" onClick={() => setView('new')} title="Tạo cuộc trò chuyện">
              ✎
            </button>
          )}
        </>
      )}
      <button className="chathead-btn" onClick={() => setOpen(false)} title="Đóng">
        ×
      </button>
    </div>
  );
}

// 2 tab đầu danh sách: Nội bộ (chat nhân viên) | Khách hàng (chat KH từ bidnhanh). Badge = số tin chưa đọc.
function ChatTabs() {
  const { tab, setTab, internalUnread, custUnread } = useChat();
  const TabBtn = ({ k, label, n }) => (
    <button type="button" className={'chattab' + (tab === k ? ' on' : '')} onClick={() => setTab(k)}>
      {label}
      {n > 0 && <span className="chattab-badge">{n > 99 ? '99+' : n}</span>}
    </button>
  );
  return (
    <div className="chattabs">
      <TabBtn k="internal" label="Nội bộ" n={internalUnread} />
      <TabBtn k="customer" label="Khách hàng" n={custUnread} />
    </div>
  );
}

// HỘI THOẠI GHIM lên đầu danh sách (riêng mỗi người, tối đa 3) — trong nhóm ghim/không ghim
// vẫn giữ thứ tự tin mới nhất (sort ổn định).
export const pinnedFirst = (arr) => [...arr].sort((a, b) => (a.pinned === b.pinned ? 0 : a.pinned ? -1 : 1));

function ConversationList() {
  const { me, convs, openConv, setView, windows, onlineIds, patchConv } = useChat();
  const togglePinConv = async (e, c) => {
    e.stopPropagation(); // đừng mở cửa sổ khi chỉ định ghim
    try {
      const r = await pinConversation(c.id, !c.pinned);
      patchConv(c.id, { pinned: r.pinned });
    } catch (err) { alert(err?.response?.data?.message || 'Ghim thất bại'); }
  };
  if (!convs.length)
    return (
      <div className="chatempty">
        Chưa có cuộc trò chuyện.
        <br />
        <button className="btn sm" style={{ marginTop: 10 }} onClick={() => setView('new')}>
          ✎ Bắt đầu chat
        </button>
      </div>
    );
  return (
    <div className="chatlist">
      {pinnedFirst(convs).map((c) => {
        const opened = windows.some((w) => String(w.id) === String(c.id));
        const online = c.type === 'direct' && c.other?.id && onlineIds?.has(String(c.other.id));
        return (
          <div
            key={c.id}
            className={'chatlist-row' + (c.unread ? ' unread' : '') + (opened ? ' active' : '')}
            onClick={() => openConv(c)}
          >
            <div className="chatlist-ava">
              <div className={'chat-avatar' + (c.type === 'group' ? ' group' : '')}>{c.type === 'group' ? '👥' : <AvaImg src={c.other?.avatar} name={c.name} />}</div>
              {online && <span className="chat-online-dot" title="Đang online" />}
              {c.unread > 0 && <span className="chatlist-dot" />}
            </div>
            <div className="chatlist-main">
              <div className="chatlist-top">
                <span className="chatlist-name">{c.pinned ? '📌 ' : ''}{c.name}</span>
                <span className="chatlist-time">{fmtTime(c.lastMessage?.at)}</span>
              </div>
              <div className="chatlist-preview">{c.lastMessage?.text || 'Bắt đầu trò chuyện…'}</div>
            </div>
            {c.unread > 0 && <span className="chatlist-badge">{c.unread}</span>}
            <button type="button" className={'chatlist-pinbtn' + (c.pinned ? ' on' : '')}
              title={c.pinned ? 'Bỏ ghim hội thoại' : 'Ghim hội thoại lên đầu (tối đa 3)'}
              onClick={(e) => togglePinConv(e, c)}>📌</button>
          </div>
        );
      })}
      {me?.role === 'GD' && <ObserverGroups openConv={openConv} windows={windows} />}
    </div>
  );
}

// (Giám đốc) GIÁM SÁT NHÓM: liệt kê mọi nhóm KHÔNG có mình — bấm mở ở chế độ CHỈ XEM.
// Thu gọn mặc định + chỉ tải khi mở (đỡ 1 request cho lần mở chat thường ngày).
function ObserverGroups({ openConv, windows }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null); // null = chưa tải
  const toggle = () => {
    setOpen((v) => !v);
    if (items === null) listObserverGroups().then(setItems).catch(() => setItems([]));
  };
  return (
    <div className="chat-observer-sec">
      <button type="button" className="chat-observer-head" onClick={toggle}>
        👁 Giám sát nhóm (không có bạn) {open ? '▾' : '▸'}
      </button>
      {open && (
        items === null ? <div className="small muted" style={{ padding: '6px 10px' }}>Đang tải…</div>
        : !items.length ? <div className="small muted" style={{ padding: '6px 10px' }}>Bạn đang ở trong tất cả các nhóm.</div>
        : items.map((c) => {
          const opened = windows.some((w) => String(w.id) === String(c.id));
          return (
            <div key={c.id} className={'chatlist-row' + (opened ? ' active' : '')} onClick={() => openConv({ ...c, observer: true })}>
              <div className="chatlist-ava"><div className="chat-avatar group">👁</div></div>
              <div className="chatlist-main">
                <div className="chatlist-top">
                  <span className="chatlist-name">{c.name}</span>
                  <span className="chatlist-time">{fmtTime(c.lastMessage?.at)}</span>
                </div>
                <div className="chatlist-preview">{(c.members || []).length} thành viên · {c.lastMessage?.text || 'Chưa có tin'}</div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

// "Offline bao lâu" từ mốc lastSeenAt tới hiện tại.
function sinceLabel(lastSeenAt) {
  if (!lastSeenAt) return 'chưa truy cập';
  const diff = Date.now() - new Date(lastSeenAt).getTime();
  if (diff < 60_000) return 'vừa xong';
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins} phút trước`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} giờ${mins % 60 ? ` ${mins % 60}p` : ''} trước`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days} ngày trước`;
  return new Date(lastSeenAt).toLocaleDateString('vi-VN');
}

// Panel "Trạng thái nhân viên": ai đang online / offline bao lâu. Online realtime theo onlineIds,
// mốc offline lấy từ API (lastSeenAt). Có thanh tìm theo tên.
function PresencePanel() {
  const { onlineIds } = useChat();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [, force] = useState(0);
  useEffect(() => {
    let alive = true;
    chatPresence().then((d) => { if (alive) setRows(d.rows || []); }).catch(() => {}).finally(() => alive && setLoading(false));
    // Cập nhật nhãn "x phút trước" mỗi 30s cho tươi.
    const t = setInterval(() => force((n) => n + 1), 30_000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  // Gộp trạng thái online realtime từ onlineIds; sắp online lên đầu.
  const list = rows
    .map((u) => ({ ...u, online: onlineIds.has(String(u.id)) || u.online }))
    .filter((u) => !q.trim() || u.name.toLowerCase().includes(q.trim().toLowerCase()))
    .sort((a, b) => (a.online === b.online ? 0 : a.online ? -1 : 1)
      || (new Date(b.lastSeenAt || 0) - new Date(a.lastSeenAt || 0)));
  const onlineCount = list.filter((u) => u.online).length;
  return (
    <div className="chatlist">
      <div style={{ padding: '8px 10px' }}>
        <input className="chat-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 Tìm nhân viên…" style={{ width: '100%' }} />
        <div className="small muted" style={{ marginTop: 6 }}>🟢 {onlineCount} đang online · {list.length - onlineCount} offline</div>
      </div>
      {loading ? <div className="chatempty">Đang tải…</div> : !list.length ? <div className="chatempty">Không có nhân viên.</div> : (
        list.map((u) => (
          <div key={u.id} className="chatlist-row" style={{ cursor: 'default' }}>
            <div className="chatlist-ava">
              <div className="chat-avatar"><AvaImg src={u.avatar} name={u.name} /></div>
              <span style={{ position: 'absolute', right: 0, bottom: 0, width: 11, height: 11, borderRadius: '50%', border: '2px solid var(--panel)', background: u.online ? '#22c55e' : '#9ca3af' }} />
            </div>
            <div className="chatlist-main">
              <div className="chatlist-top">
                <span className="chatlist-name">{u.name}</span>
                <span className="chatlist-time" style={{ color: u.online ? '#16a34a' : 'var(--muted)' }}>{u.online ? 'Online' : sinceLabel(u.lastSeenAt)}</span>
              </div>
              <div className="chatlist-preview">{(u.departments || []).join(', ') || u.role}</div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

const MENTION_ALL = 'Tất cả';
// Thu hồi tin: chỉ trong 10 phút kể từ khi gửi (khớp RECALL_WINDOW_MS ở backend).
const RECALL_WINDOW_MS = 10 * 60 * 1000;
const canRecall = (m, myId) => !m.recalled && !m.pending && !m.failed && String(m.sender?._id) === myId
  && Date.now() - new Date(m.createdAt).getTime() < RECALL_WINDOW_MS;
// Nhắc lại thông báo: tin CỦA MÌNH, chưa thu hồi, và có TAG ai đó (nhóm) hoặc là chat riêng 1-1.
const canRemind = (m, myId, isGroup) => !m.recalled && !m.pending && !m.failed && String(m.sender?._id) === myId
  && (!isGroup || m.mentionAll || (m.mentions || []).length > 0);
const ROLE_LABEL = { GD: 'Giám đốc', CV: 'Cố vấn', TP: 'Trưởng phòng', NV: 'Nhân viên' };

// Tô màu các đoạn @tên trong nội dung tin nhắn.
function renderWithMentions(text, names) {
  if (!names.length) return text;
  const sorted = [...names].sort((a, b) => b.length - a.length).map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp('@(' + sorted.join('|') + ')', 'g');
  const out = [];
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(
      <span className="chat-mention" key={i++}>
        {m[0]}
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// Một cửa sổ hội thoại độc lập (mở được nhiều cái cùng lúc).
// export: trang "Tin nhắn" (ChatPage) nhúng lại cửa sổ này làm cột giữa — mọi tính năng dùng chung.
// onTitleClick (chỉ ChatPage truyền): bấm TÊN trên đầu khung -> mở/đóng bảng thông tin bên phải.
export function ChatWindow({ conv, front, onTitleClick }) {
  const { me, convs, refresh, subscribeMessages, subscribeRead, setConvMinimized, patchConv, startDirect, onlineIds, bumpConv, closeWindow, setOpen } = useChat();
  const isMobile = () => typeof window !== 'undefined' && window.innerWidth <= 980;
  const toast = useToast();
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(true);
  const [text, setText] = useState('');
  const [men, setMen] = useState(null); // { at, q } khi đang gõ @
  const [atts, setAtts] = useState([]); // đính kèm đang chờ gửi
  const [replyTo, setReplyTo] = useState(null); // { id, senderId, senderName, text } tin đang trả lời
  const [imgView, setImgView] = useState(null); // ảnh đang xem to (lightbox) — src
  const [dragOver, setDragOver] = useState(false);
  const [sending, setSending] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [minimized, setMinimized] = useState(false); // thu nhỏ về thanh tiêu đề
  const [minUnread, setMinUnread] = useState(0); // số tin mới nhận khi đang thu nhỏ
  const [showMembers, setShowMembers] = useState(false); // bảng thành viên nhóm
  const [showAddMember, setShowAddMember] = useState(false); // ô chọn người để thêm
  const [allUsers, setAllUsers] = useState([]); // danh sách nhân viên (để thêm)
  const [addQ, setAddQ] = useState(''); // tìm khi thêm
  const [memberBusy, setMemberBusy] = useState(false);
  const [profileUser, setProfileUser] = useState(null); // thẻ thông tin nhân viên đang xem
  const [typingNames, setTypingNames] = useState([]); // ai đang gõ (hiện "… đang gõ")
  const [reactPickerFor, setReactPickerFor] = useState(null); // id tin đang mở bảng chọn cảm xúc
  const [peek, setPeek] = useState(null); // popover xem chi tiết: { title, lines:[{icon,name}] }
  // Kích thước cửa sổ do NGƯỜI DÙNG tự chỉnh (lưu localStorage, dùng chung cho mọi cửa sổ chat).
  const [size, setSize] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem('ft_chat_size') || 'null');
      if (s && s.w && s.h) return { w: s.w, h: s.h };
    } catch {}
    return null;
  });
  const [readMap, setReadMap] = useState(() => {
    // Thời điểm ĐỌC gần nhất của từng thành viên khác (ms) — để hiện dấu Đã xem.
    const my = String(me.id || me._id);
    const m = {};
    (conv.members || []).forEach((mm) => {
      if (String(mm.id) !== my && mm.readAt) m[String(mm.id)] = new Date(mm.readAt).getTime();
    });
    return m;
  });
  const emojiBtnRef = useRef(null);
  const minimizedRef = useRef(false); // gương của `minimized` cho callback socket (không cần re-subscribe)
  const typingSentRef = useRef(false); // đã gửi "đang gõ" cho đối phương chưa
  const typingStopRef = useRef(null); // timer dừng gõ
  const typingInRef = useRef({}); // name -> timer xoá khi ngừng nhận
  const endRef = useRef(null);
  const msgsRef = useRef(null); // khung cuộn tin nhắn (đo vị trí đang đọc)
  const lastMsgIdRef = useRef(null); // id tin cuối đã xử lý cuộn (phân biệt tin MỚI với cập nhật cảm xúc)
  const [newBelow, setNewBelow] = useState(0); // số tin mới đến khi đang đọc tin cũ (hiện mũi tên ↓)
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const unreadRef = useRef(null);
  const initRef = useRef(false);
  const unreadSnapRef = useRef(conv.unread || 0); // số tin chưa đọc lúc mở

  const myId = String(me.id || me._id);
  const isGroup = conv.type === 'group';
  // CHẾ ĐỘ GIÁM SÁT (GD mở nhóm KHÔNG có mình): THUẦN XEM — ẩn ô nhập + mọi nút tương tác,
  // không markRead/"đã xem", không mute. Backend vẫn chặn mọi endpoint ghi (ensureMember).
  const observer = !!conv.observer;
  // TIN GHIM (kiểu Zalo, tối đa 3 — CHUNG cả hội thoại): đọc từ danh sách "sống" trong context
  // để người khác ghim/bỏ ghim là dải ghim tự đổi (refresh); cửa sổ giám sát không nằm trong
  // danh sách -> dùng bản chụp lúc mở.
  const liveConv = convs.find((c) => String(c.id) === String(conv.id)) || conv;
  const pinnedMsgs = liveConv.pinnedMessages || [];
  const pinnedIds = new Set(pinnedMsgs.map((p) => String(p.msgId)));
  const pinnedKey = pinnedMsgs.map((p) => p.msgId).join(','); // cho memo danh sách tin
  const [pinsOpen, setPinsOpen] = useState(false); // dải ghim đang mở rộng (hiện đủ 3) hay thu gọn (1)
  const recipientIds = (conv.members || []).filter((mm) => String(mm.id) !== myId).map((mm) => mm.id);

  // Thu nhỏ / mở lại cửa sổ. Khi mở lại: xoá badge + đánh dấu đã đọc.
  const toggleMinimize = useCallback((next) => {
    const on = typeof next === 'boolean' ? next : !minimizedRef.current;
    minimizedRef.current = on;
    setMinimized(on);
    setConvMinimized(conv.id, on);
    if (!on) { setMinUnread(0); if (!conv.observer) import('../api/chat').then(({ markRead }) => markRead(conv.id).catch(() => {})); }
  }, [conv.id, setConvMinimized]);

  // Dọn trạng thái thu nhỏ khỏi context khi đóng cửa sổ.
  useEffect(() => () => setConvMinimized(conv.id, false), [conv.id, setConvMinimized]);

  // Thêm thành viên: người TẠO nhóm hoặc Giám đốc/Cố vấn.
  const isCreator = isGroup && String(conv.createdBy || '') === myId;
  const canAddMember = isGroup && (me.role === 'GD' || me.role === 'CV' || isCreator);
  // Xoá thành viên: CHỈ người tạo nhóm.
  const canRemoveMember = isCreator;
  // Mở ô "Thêm thành viên": nạp danh sách nhân viên 1 lần.
  const openAddMember = useCallback(() => {
    setShowAddMember((v) => !v);
    if (!allUsers.length) chatUsers().then(setAllUsers).catch(() => {});
  }, [allUsers.length]);
  const addMember = useCallback(async (uid) => {
    if (memberBusy) return;
    setMemberBusy(true);
    try {
      const updated = await addGroupMember(conv.id, uid);
      patchConv(conv.id, { members: updated.members, name: updated.name });
      setAddQ('');
      setShowAddMember(false);
    } catch (e) {
      alert(e?.response?.data?.message || 'Thêm thành viên thất bại');
    } finally { setMemberBusy(false); }
  }, [conv.id, memberBusy, patchConv]);
  const removeMember = useCallback(async (uid, name) => {
    if (memberBusy) return;
    if (!window.confirm(`Xoá ${name} khỏi nhóm?`)) return;
    setMemberBusy(true);
    try {
      const updated = await removeGroupMember(conv.id, uid);
      patchConv(conv.id, { members: updated.members });
    } catch (e) {
      alert(e?.response?.data?.message || 'Xoá thành viên thất bại');
    } finally { setMemberBusy(false); }
  }, [conv.id, memberBusy, patchConv]);
  // Đổi tên nhóm: MỌI thành viên đều được đổi.
  const renameGroupChat = useCallback(async () => {
    if (memberBusy) return;
    const next = window.prompt('Đổi tên nhóm:', conv.name || '');
    if (next == null) return;
    const name = next.trim();
    if (!name || name === conv.name) return;
    setMemberBusy(true);
    try {
      const updated = await renameGroup(conv.id, name);
      patchConv(conv.id, { name: updated.name });
    } catch (e) {
      alert(e?.response?.data?.message || 'Đổi tên nhóm thất bại');
    } finally { setMemberBusy(false); }
  }, [conv.id, conv.name, memberBusy, patchConv]);

  // Bấm tên/avatar 1 người -> thẻ thông tin (phòng ban + chức vụ). Nạp danh sách NV để lấy phòng ban.
  const openProfile = useCallback(async (member) => {
    if (!member?.id) return;
    let users = allUsers;
    if (!users.length) { try { users = await chatUsers(); setAllUsers(users); } catch { users = []; } }
    const u = users.find((x) => String(x.id) === String(member.id)) || {};
    setProfileUser({ id: member.id, name: member.name || u.name || '', role: member.role || u.role || '', departments: u.departments || [], avatar: member.avatar || u.avatar || '' });
  }, [allUsers]);
  // Nhắn tin riêng với người đang xem thẻ.
  const chatWithProfile = useCallback(() => {
    const p = profileUser;
    if (!p || String(p.id) === myId) return;
    setProfileUser(null);
    startDirect(p.id).catch(() => {});
  }, [profileUser, myId, startDirect]);

  // Báo "đang gõ" cho đối phương: gửi bật 1 lần, tự tắt sau 2,5s ngừng gõ.
  const notifyTyping = useCallback((on) => {
    if (on) {
      if (!typingSentRef.current) { typingSentRef.current = true; emitTyping(conv.id, recipientIds, true); }
      clearTimeout(typingStopRef.current);
      typingStopRef.current = setTimeout(() => { typingSentRef.current = false; emitTyping(conv.id, recipientIds, false); }, 2500);
    } else {
      clearTimeout(typingStopRef.current);
      if (typingSentRef.current) { typingSentRef.current = false; emitTyping(conv.id, recipientIds, false); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv.id]);

  // Dừng báo gõ + dọn timer khi đóng cửa sổ / đổi hội thoại.
  useEffect(() => () => { notifyTyping(false); Object.values(typingInRef.current).forEach(clearTimeout); }, [notifyTyping]);

  // Tải tin nhắn khi mở cửa sổ.
  useEffect(() => {
    let alive = true;
    setLoadingMsgs(true);
    initRef.current = false;
    unreadSnapRef.current = conv.unread || 0;
    listMessages(conv.id, { limit: 40 })
      .then((msgs) => alive && setMessages(msgs))
      .finally(() => alive && setLoadingMsgs(false));
    return () => {
      alive = false;
    };
  }, [conv.id]);

  // Nhận tin mới realtime cho riêng hội thoại này.
  useEffect(() => {
    const unsub = subscribeMessages(conv.id, (payload) => {
      // Nhận tin của người khác khi đang thu nhỏ -> đếm badge chưa đọc trên thanh tiêu đề.
      if (minimizedRef.current && String(payload.sender?._id) !== String(me.id || me._id) && !payload.clientId) {
        setMinUnread((n) => n + 1);
      }
      setMessages((prev) => {
        // Khớp tin tạm (gửi tức thì) theo clientId -> thay bằng bản thật từ server.
        if (payload.clientId && prev.some((m) => m.clientId && m.clientId === payload.clientId)) {
          return prev.map((m) => (m.clientId === payload.clientId ? { ...payload } : m));
        }
        return prev.some((m) => String(m._id) === String(payload._id)) ? prev : [...prev, payload];
      });
    });
    return unsub;
  }, [conv.id, subscribeMessages]); // eslint-disable-line react-hooks/exhaustive-deps

  // DỰ PHÒNG khi socket chập chờn (VD web thật sau proxy không giữ được WebSocket): cứ ~5s tải lại
  // tin mới nhất và gộp vào -> tin của người khác vẫn hiện mà KHỎI PHẢI F5. Giữ nguyên tin đang gửi/lỗi.
  useEffect(() => {
    const tick = () => {
      if (document.hidden) return;
      listMessages(conv.id, { limit: 40 })
        .then((msgs) => setMessages((prev) => {
          const serverIds = new Set(msgs.map((m) => String(m._id)));
          const pending = prev.filter((m) => m.clientId && (m.pending || m.failed) && !serverIds.has(String(m._id)));
          const next = [...msgs, ...pending];
          if (next.length === prev.length && next.every((m, i) => String(m._id) === String(prev[i]?._id))) return prev;
          return next;
        }))
        .catch(() => {});
    };
    const t = setInterval(tick, 5000);
    return () => clearInterval(t);
  }, [conv.id]);

  // Nhận "đã xem" từ đối phương -> cập nhật readMap để render dấu Đã xem.
  useEffect(() => {
    const unsub = subscribeRead(conv.id, ({ user, at }) => {
      setReadMap((prev) => ({ ...prev, [String(user)]: new Date(at).getTime() }));
    });
    return unsub;
  }, [conv.id, subscribeRead]);

  // Nhận "đang gõ" từ người khác trong hội thoại này.
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const onTyping = (evt) => {
      if (String(evt.conversation) !== String(conv.id)) return;
      if (String(evt.from) === String(me.id || me._id)) return;
      const nm = evt.name || 'Ai đó';
      if (evt.typing) {
        setTypingNames((prev) => (prev.includes(nm) ? prev : [...prev, nm]));
        clearTimeout(typingInRef.current[nm]);
        typingInRef.current[nm] = setTimeout(() => {
          setTypingNames((prev) => prev.filter((x) => x !== nm));
          delete typingInRef.current[nm];
        }, 4000);
      } else {
        clearTimeout(typingInRef.current[nm]);
        delete typingInRef.current[nm];
        setTypingNames((prev) => prev.filter((x) => x !== nm));
      }
    };
    socket.on('chat:typing', onTyping);
    return () => socket.off('chat:typing', onTyping);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv.id]);

  // Khi socket (re)kết nối lại (sau khi backend chập chờn) -> nạp lại tin để không phải F5.
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const onReconnect = () => listMessages(conv.id, { limit: 40 }).then(setMessages).catch(() => {});
    socket.on('connect', onReconnect);
    return () => socket.off('connect', onReconnect);
  }, [conv.id]);

  // Nhận cảm xúc (react) realtime -> cập nhật reactions của đúng tin.
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const onReaction = (p) => {
      if (String(p.conversation) !== String(conv.id)) return;
      setMessages((prev) => prev.map((m) => (String(m._id) === String(p.messageId) ? { ...m, reactions: p.reactions } : m)));
    };
    socket.on('chat:reaction', onReaction);
    return () => socket.off('chat:reaction', onReaction);
  }, [conv.id]);

  // Đang ở gần đáy khung tin nhắn? (đọc tin cũ = cuộn lên xa đáy)
  const isNearBottom = () => {
    const el = msgsRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };
  const jumpToLatest = () => {
    // Gán scrollTop trực tiếp thay vì scrollIntoView({smooth}): smooth có môi trường bị vô hiệu
    // (đã gặp khi kiểm thử) -> bấm mũi tên mà không nhảy. Quãng cuộn dài, nhảy thẳng còn rõ hơn.
    const el = msgsRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    setNewBelow(0);
  };

  // Lần đầu mở: cuộn tới ngay đầu các tin chưa đọc (nếu có), ngược lại xuống cuối.
  // Tin MỚI đến sau đó: chỉ tự cuộn khi (a) tin của MÌNH vừa gửi, hoặc (b) đang ở gần đáy.
  // Đang kéo lên đọc tin cũ -> GIỮ NGUYÊN vị trí, hiện mũi tên "↓ tin mới" để bấm xuống.
  useEffect(() => {
    if (!messages.length) return;
    const last = messages[messages.length - 1];
    const lastId = String(last.clientId || last._id);
    if (!initRef.current) {
      initRef.current = true;
      lastMsgIdRef.current = lastId;
      if (unreadRef.current) unreadRef.current.scrollIntoView({ block: 'start' });
      else endRef.current?.scrollIntoView();
      return;
    }
    if (lastMsgIdRef.current === lastId) return; // chỉ cảm xúc/đã xem đổi -> không cuộn
    lastMsgIdRef.current = lastId;
    if (String(last.sender?._id) === myId || isNearBottom()) {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      setNewBelow((n) => n + 1);
    }
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (atts.length) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [atts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ô nhập tự cao theo nội dung đang gõ, TỐI ĐA ~5 dòng (112px, khớp max-height CSS) rồi cuộn
  // trong ô. Đặt 'auto' trước khi đo, nếu không scrollHeight giữ chiều cao cũ khi xoá bớt chữ.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 112) + 'px';
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  function addFiles(fileList) {
    const items = [...fileList].map((f) => ({
      file: f,
      name: f.name,
      type: f.type,
      size: f.size,
      preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
    }));
    setAtts((prev) => [...prev, ...items]);
  }
  function onPaste(e) {
    const imgs = [...(e.clipboardData?.items || [])].filter((it) => it.kind === 'file').map((it) => it.getAsFile()).filter(Boolean);
    if (imgs.length) {
      e.preventDefault();
      addFiles(imgs);
    }
  }
  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  }

  // Lựa chọn để tag: @Tất cả + các thành viên khác.
  const mentionOptions = isGroup
    ? [{ id: 'all', name: MENTION_ALL }, ...(conv.members || []).filter((mm) => String(mm.id) !== myId)]
    : [];
  const mentionNames = mentionOptions.map((o) => o.name);
  const mentionKey = mentionNames.join('|');
  const suggestions =
    men && isGroup ? mentionOptions.filter((o) => o.name.toLowerCase().includes((men.q || '').toLowerCase())) : [];

  function onChange(e) {
    const val = e.target.value;
    setText(val);
    notifyTyping(val.trim().length > 0); // báo/tắt "đang gõ"
    if (!isGroup) return;
    const caret = e.target.selectionStart;
    const upto = val.slice(0, caret);
    const at = upto.lastIndexOf('@');
    if (at >= 0 && (at === 0 || /\s/.test(upto[at - 1]))) setMen({ at, q: upto.slice(at + 1) });
    else setMen(null);
  }

  // Click ra ngoài bảng sticker/emoji (không phải nút mở) -> tự đóng bảng.
  useEffect(() => {
    if (!showEmoji) return;
    const onDown = (e) => {
      if (e.target.closest?.('.emoji-pop')) return; // click trong bảng
      if (emojiBtnRef.current?.contains(e.target)) return; // click nút mở (đã tự toggle)
      setShowEmoji(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showEmoji]);

  // Chèn emoji vào ô nhập tại vị trí con trỏ (ổn định).
  const insertEmoji = useCallback((emo) => {
    const el = inputRef.current;
    const start = el ? el.selectionStart : null;
    const end = el ? el.selectionEnd : null;
    setText((prev) => {
      const a = start ?? prev.length;
      const b = end ?? prev.length;
      return prev.slice(0, a) + emo + prev.slice(b);
    });
    setTimeout(() => {
      el?.focus();
      const p = (start ?? 0) + emo.length;
      el?.setSelectionRange(p, p);
    }, 0);
  }, []);

  // Gửi sticker = gửi luôn 1 tin chỉ gồm emoji (sẽ render to).
  const sendSticker = useCallback(
    async (emo) => {
      setShowEmoji(false);
      try {
        const msg = await sendMessage(conv.id, emo, {});
        setMessages((prev) => (prev.some((m) => String(m._id) === String(msg._id)) ? prev : [...prev, msg]));
        bumpConv(conv.id, msg);
      } catch (err) {
        alert(err?.response?.data?.message || err?.message || 'Gửi sticker thất bại');
      }
    },
    [conv.id, bumpConv]
  );

  function pickMention(o) {
    const end = men.at + 1 + (men.q || '').length;
    const next = text.slice(0, men.at) + '@' + o.name + ' ' + text.slice(end);
    setText(next);
    setMen(null);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  const saveNote = useCallback(
    async (m) => {
      const content = (m.text || '').trim() || (m.attachments || []).map((a) => a.name).join(', ') || '[Đính kèm]';
      try {
        await createNote({ content, source: 'chat', fromName: m.sender?.name || '' });
        toast('Đã lưu vào ghi chú cá nhân');
      } catch {
        toast('Không lưu được ghi chú', 'danger');
      }
    },
    [toast]
  );

  // Tin chưa đọc đầu tiên = tin ĐẾN thứ N tính từ cuối (N = số chưa đọc lúc mở).
  const firstUnreadId = (() => {
    if (observer) return null; // giám sát: không có "chưa đọc"
    const n = unreadSnapRef.current;
    if (!n) return null;
    const incoming = messages.filter((m) => String(m.sender?._id) !== myId);
    if (!incoming.length) return null;
    return incoming[Math.max(0, incoming.length - n)]?._id || null;
  })();

  async function submit(e) {
    e?.preventDefault();
    const t = text.trim();
    if ((!t && !atts.length) || sending) return;
    const mentionAll = isGroup && t.includes('@' + MENTION_ALL);
    let mentions = isGroup
      ? (conv.members || []).filter((mm) => String(mm.id) !== myId && t.includes('@' + mm.name)).map((mm) => mm.id)
      : [];
    const reply = replyTo; // tin đang trả lời (nếu có)
    // Trả lời tin người khác trong nhóm -> TỰ TAG (mention) người đó để họ được báo.
    if (reply?.senderId && isGroup && String(reply.senderId) !== myId && !mentions.includes(reply.senderId)) mentions = [...mentions, reply.senderId];
    const replySnap = reply ? { message: reply.id, senderName: reply.senderName, text: reply.text } : null;
    const sentAtts = atts;
    setText('');
    setMen(null);
    setAtts([]);
    setReplyTo(null);
    notifyTyping(false); // gửi = ngừng gõ

    // Đường nhanh: tin CHỈ có chữ -> hiện ngay (optimistic), gửi ngầm, thay bằng bản thật khi xong.
    if (!sentAtts.length) {
      const clientId = 'tmp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
      const temp = { _id: clientId, clientId, sender: { _id: myId, name: me.name }, text: t, attachments: [], mentions, mentionAll, replyTo: replySnap, createdAt: new Date().toISOString(), pending: true };
      setMessages((prev) => [...prev, temp]);
      try {
        const msg = await sendMessage(conv.id, t, { mentions, mentionAll, clientId, replyTo: reply?.id });
        setMessages((prev) => {
          // Bản thật có thể đã tới qua socket (khớp clientId); dù cách nào cũng chỉ còn 1 tin.
          if (prev.some((m) => String(m._id) === String(msg._id) && m.clientId !== clientId)) {
            return prev.filter((m) => m.clientId !== clientId);
          }
          return prev.map((m) => (m.clientId === clientId ? { ...msg } : m));
        });
        bumpConv(conv.id, msg);
      } catch {
        // Đánh dấu lỗi để hiện "Gửi lại".
        setMessages((prev) => prev.map((m) => (m.clientId === clientId ? { ...m, pending: false, failed: true } : m)));
      }
      return;
    }

    // Có đính kèm: upload trước rồi hiển thị (không optimistic vì cần thời gian tải tệp).
    setSending(true);
    try {
      const metas = await uploadChatFiles(sentAtts.map((a) => a.file));
      const msg = await sendMessage(conv.id, t, { mentions, mentionAll, attachments: metas, replyTo: reply?.id });
      setMessages((prev) => (prev.some((m) => String(m._id) === String(msg._id)) ? prev : [...prev, msg]));
      bumpConv(conv.id, msg);
      sentAtts.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
    } catch (err) {
      setText(t);
      setAtts(sentAtts);
      alert(err?.response?.data?.message || err?.message || 'Gửi tệp thất bại');
    } finally {
      setSending(false);
    }
  }

  // Gửi lại 1 tin bị lỗi (đường nhanh chỉ có chữ).
  const resend = useCallback(async (failed) => {
    setMessages((prev) => prev.map((m) => (m.clientId === failed.clientId ? { ...m, pending: true, failed: false } : m)));
    try {
      const msg = await sendMessage(conv.id, failed.text, { mentions: failed.mentions, mentionAll: failed.mentionAll, clientId: failed.clientId });
      setMessages((prev) => prev.map((m) => (m.clientId === failed.clientId ? { ...msg } : m)));
      bumpConv(conv.id, msg);
    } catch {
      setMessages((prev) => prev.map((m) => (m.clientId === failed.clientId ? { ...m, pending: false, failed: true } : m)));
    }
  }, [conv.id, bumpConv]);

  // Bắt đầu trả lời 1 tin (reply kiểu Zalo): lưu tin gốc + focus ô nhập.
  // TẮT/BẬT âm hội thoại (riêng mình): tắt thì không kêu, không popup, không push.
  const muted = !!conv.muted;
  const doMute = useCallback(async () => {
    try {
      const r = await muteConversation(conv.id, !muted);
      patchConv(conv.id, { muted: r.muted });
      toast(r.muted ? '🔕 Đã tắt âm hội thoại này' : '🔔 Đã bật lại thông báo');
    } catch (e) { toast(e?.response?.data?.message || 'Không đổi được', 'danger'); }
  }, [conv.id, muted, patchConv, toast]);

  // Nhắc lại thông báo cho người được TAG (nhóm) / người kia (chat riêng) khi chưa thấy phản hồi.
  const doRemind = useCallback(async (m) => {
    try {
      const r = await remindMessage(conv.id, m._id);
      const who = (r.names || []).join(', ');
      toast(`🔔 Đã nhắc lại${who ? ` cho ${who}` : ''}${r.sent < r.targets ? ' (có người đang tắt âm)' : ''}`);
    } catch (e) { toast(e?.response?.data?.message || 'Nhắc lại thất bại', 'danger'); }
  }, [conv.id, toast]);

  // Thu hồi tin của mình (trong 10'): server xoá nội dung, mọi người thấy "đã thu hồi".
  const doRecall = useCallback(async (m) => {
    if (!window.confirm('Thu hồi tin nhắn này? Mọi người sẽ thấy "Tin nhắn đã được thu hồi".')) return;
    try {
      await recallMessage(conv.id, m._id);
      setMessages((prev) => prev.map((x) => (String(x._id) === String(m._id) ? { ...x, recalled: true, text: '', attachments: [] } : x)));
    } catch (e) { toast(e?.response?.data?.message || 'Thu hồi thất bại', 'danger'); }
  }, [conv.id, toast]);

  // XOÁ CHỈ Ở PHÍA MÌNH (kiểu Zalo): ẩn tin khỏi màn của mình, NGƯỜI KHÁC VẪN THẤY.
  // Tin bất kỳ (của mình lẫn người khác), không giới hạn thời gian. Không cần socket — chỉ mình đổi.
  const doDeleteForMe = useCallback(async (m) => {
    if (!window.confirm('Xoá tin nhắn này CHỈ Ở PHÍA BẠN? Người khác vẫn nhìn thấy bình thường.')) return;
    try {
      await deleteMessageForMe(conv.id, m._id);
      setMessages((prev) => prev.filter((x) => String(x._id) !== String(m._id)));
    } catch (e) { toast(e?.response?.data?.message || 'Xoá thất bại', 'danger'); }
  }, [conv.id, toast]);

  // GHIM TIN lên đầu đoạn chat (kiểu Zalo, tối đa 3 — của CHUNG hội thoại, ai cũng ghim được).
  // refresh() nạp lại danh sách hội thoại -> dải ghim (đọc từ convs) tự cập nhật.
  const doPinMsg = useCallback(async (m) => {
    try {
      await pinMessage(conv.id, m._id);
      refresh();
      toast('📌 Đã ghim tin lên đầu đoạn chat');
    } catch (e) { toast(e?.response?.data?.message || 'Ghim thất bại', 'danger'); }
  }, [conv.id, toast, refresh]);
  const doUnpinMsg = useCallback(async (msgId) => {
    try {
      await unpinMessage(conv.id, msgId);
      refresh();
      toast('Đã bỏ ghim tin');
    } catch (e) { toast(e?.response?.data?.message || 'Bỏ ghim thất bại', 'danger'); }
  }, [conv.id, toast, refresh]);

  // Nhận sự kiện thu hồi từ người khác -> cập nhật ngay.
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const onRecall = (e) => {
      if (String(e.conversation) !== String(conv.id)) return;
      setMessages((prev) => prev.map((x) => (String(x._id) === String(e._id) ? { ...x, recalled: true, text: '', attachments: [] } : x)));
    };
    socket.on('chat:recall', onRecall);
    return () => socket.off('chat:recall', onRecall);
  }, [conv.id]);

  const startReply = useCallback((m) => {
    const preview = m.text || ((m.attachments || []).length ? 'đã gửi tệp đính kèm' : '');
    setReplyTo({ id: m._id, senderId: m.sender?._id, senderName: m.sender?.name || '', text: String(preview).slice(0, 200) });
    // TỰ CHÈN "@Tên " người được trả lời vào ĐẦU ô nhập (nhóm, tin người khác) — để thấy rõ
    // mình đang tag ai ngay trong nội dung. (Server vốn đã tự mention người được reply để báo,
    // nhưng tag không HIỆN trong tin nên người nhận tưởng không được tag.)
    const name = m.sender?.name;
    if (isGroup && name && String(m.sender?._id) !== myId) {
      setText((t) => (t.includes('@' + name) ? t : '@' + name + ' ' + t));
    }
    setTimeout(() => {
      const el = inputRef.current;
      if (el) { el.focus(); const n = el.value.length; el.setSelectionRange(n, n); } // con trỏ về CUỐI, sau tag
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroup, myId]);
  // Thả / đổi / bỏ cảm xúc 1 tin (chỉ tin người khác). Cập nhật lạc quan rồi gọi API.
  const doReact = useCallback((m, emoji) => {
    setReactPickerFor(null);
    setMessages((prev) => prev.map((mm) => {
      if (String(mm._id) !== String(m._id)) return mm;
      const others = (mm.reactions || []).filter((r) => String(r.user) !== myId);
      const cur = (mm.reactions || []).find((r) => String(r.user) === myId);
      const next = !emoji || (cur && cur.emoji === emoji) ? others : [...others, { user: myId, name: me.name, emoji }];
      return { ...mm, reactions: next };
    }));
    reactMessage(m._id, emoji).catch(() => listMessages(conv.id, { limit: 40 }).then(setMessages).catch(() => {}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myId, me.name, conv.id]);

  // Bấm vào trích dẫn -> cuộn tới tin gốc + nháy sáng.
  const scrollToMsg = useCallback((id) => {
    const el = id && document.getElementById('cmsg-' + id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('chatmsg-flash');
    setTimeout(() => el.classList.remove('chatmsg-flash'), 1400);
  }, []);

  // Danh sách tin nhắn memo hoá -> gõ phím / chèn emoji không render lại cả danh sách.
  const messageEls = useMemo(
    () =>
      messages.map((m) => {
        const mine = String(m.sender?._id) === myId;
        const isFirstUnread = String(m._id) === String(firstUnreadId);
        return (
          <div className="chatmsg-row" key={m._id} id={'cmsg-' + m._id}>
            {isFirstUnread && (
              <div className="chat-newdivider" ref={unreadRef}>
                <span>Tin nhắn mới</span>
              </div>
            )}
            <div className={'chatmsg' + (mine ? ' mine' : '') + (m.pending ? ' pending' : '') + (m.failed ? ' failed' : '')}>
              {!mine && isGroup && (
                <div className="chatmsg-who chat-members-row--click" title="Xem thông tin nhân viên"
                  onClick={() => openProfile({ id: m.sender?._id, name: m.sender?.name, role: m.sender?.role })}>
                  <span className="chat-avatar xs"><AvaImg src={m.sender?.avatar} name={m.sender?.name} /></span>
                  {m.sender?.name}
                </div>
              )}
              <div className="chatmsg-line">
                <div className="chatmsg-content">
                  {m.replyTo && (
                    <div className="chatmsg-quote" onClick={() => scrollToMsg(m.replyTo.message)} title="Xem tin gốc">
                      <span className="chatmsg-quote-name">{m.replyTo.senderName || 'Tin nhắn'}</span>
                      <span className="chatmsg-quote-text">{m.replyTo.text || 'đã gửi tệp đính kèm'}</span>
                    </div>
                  )}
                  {/* Thân tin: click (tin người khác) để hiện bảng cảm xúc; nút reply/ghi chú ở GÓC. */}
                  <div
                    className={'chatmsg-body' + (mine ? ' mine' : '')}
                    onClick={!mine && !observer ? () => setReactPickerFor((v) => (v === m._id ? null : m._id)) : undefined}
                    title={!mine && !observer ? 'Bấm để thả cảm xúc' : undefined}
                  >
                    {m.recalled && (
                      <div className="chatmsg-bubble chatmsg-recalled">🚫 Tin nhắn đã được thu hồi</div>
                    )}
                    {!m.recalled && m.text && (
                      <div className={'chatmsg-bubble' + (isBigEmoji(m.text) ? ' big-emoji' : '')}>
                        {renderWithMentions(m.text, mentionNames)}
                      </div>
                    )}
                    {(m.recalled ? [] : m.attachments || []).map((a, i) => (
                      <div className="chatmsg-att" key={i}>
                        <AttachmentView a={a} />
                      </div>
                    ))}
                    {/* Icon nhỏ ở góc bong bóng (hiện khi rê chuột). Tin đã thu hồi vẫn có góc —
                        để còn "Xoá ở phía tôi" dọn cái khung "đã thu hồi" khỏi màn mình.
                        GIÁM SÁT: ẩn hết (chỉ xem, không tác động). */}
                    {!observer && (
                    <div className="chatmsg-corner">
                      {!m.recalled && (
                        <>
                          <button className="chatmsg-cbtn" title="Trả lời tin này" onClick={(e) => { e.stopPropagation(); startReply(m); }}>↩</button>
                          <button className="chatmsg-cbtn" title="Lưu vào ghi chú cá nhân" onClick={(e) => { e.stopPropagation(); saveNote(m); }}>🔖</button>
                          {!m.pending && !m.failed && (
                            <button className="chatmsg-cbtn" title={pinnedIds.has(String(m._id)) ? 'Bỏ ghim tin này' : 'Ghim tin lên đầu đoạn chat (tối đa 3)'}
                              onClick={(e) => { e.stopPropagation(); if (pinnedIds.has(String(m._id))) doUnpinMsg(m._id); else doPinMsg(m); }}>📌</button>
                          )}
                          {canRemind(m, myId, isGroup) && (
                            <button className="chatmsg-cbtn" title={isGroup ? 'Nhắc lại thông báo cho người được tag' : 'Nhắc lại thông báo cho người nhận'}
                              onClick={(e) => { e.stopPropagation(); doRemind(m); }}>🔔</button>
                          )}
                          {canRecall(m, myId) && (
                            <button className="chatmsg-cbtn" title="Thu hồi tin nhắn (trong 10 phút)" onClick={(e) => { e.stopPropagation(); doRecall(m); }}>🗑</button>
                          )}
                        </>
                      )}
                      {/* Xoá CHỈ Ở PHÍA MÌNH: tin bất kỳ (kể cả của người khác / đã thu hồi), trừ tin đang gửi/lỗi. */}
                      {!m.pending && !m.failed && (
                        <button className="chatmsg-cbtn" title="Xoá chỉ ở phía tôi (người khác vẫn thấy)" onClick={(e) => { e.stopPropagation(); doDeleteForMe(m); }}>✖</button>
                      )}
                    </div>
                    )}
                    {/* Bảng chọn cảm xúc: chỉ hiện khi CLICK tin của người khác */}
                    {!mine && reactPickerFor === m._id && (
                      <div className="chatmsg-react-picker" onClick={(e) => e.stopPropagation()} onMouseLeave={() => setReactPickerFor(null)}>
                        {REACTION_EMOJIS.map((e) => (
                          <button key={e} type="button" className="chatmsg-react-opt" onClick={() => doReact(m, e)}>{e}</button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {(m.reactions || []).length > 0 && (
                <div className="chatmsg-reacts">
                  {Object.entries(
                    (m.reactions || []).reduce((acc, r) => {
                      (acc[r.emoji] = acc[r.emoji] || []).push(r.name || 'Ai đó');
                      return acc;
                    }, {})
                  ).map(([emoji, names]) => {
                    const meReacted = (m.reactions || []).some((r) => String(r.user) === myId && r.emoji === emoji);
                    return (
                      <button
                        key={emoji}
                        type="button"
                        className={'chatmsg-react-badge' + (meReacted ? ' me' : '')}
                        title="Xem ai đã thả cảm xúc"
                        onClick={() =>
                          setPeek({
                            title: 'Cảm xúc',
                            lines: (m.reactions || []).map((r) => ({ icon: r.emoji, name: r.name || 'Ai đó' })),
                          })
                        }
                      >
                        <span>{emoji}</span> {names.length}
                      </button>
                    );
                  })}
                </div>
              )}
              {mine && m.failed ? (
                <div className="chatmsg-time" style={{ color: 'var(--danger)' }}>
                  Gửi lỗi · <button type="button" className="chatmsg-resend" onClick={() => resend(m)}>Gửi lại</button>
                </div>
              ) : (
                <div className="chatmsg-time">{m.pending ? 'Đang gửi…' : fmtMsgTime(m.createdAt)}</div>
              )}
            </div>
          </div>
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messages, myId, isGroup, observer, firstUnreadId, mentionKey, reactPickerFor, doReact, saveNote, resend, startReply, scrollToMsg, openProfile, pinnedKey, doPinMsg, doUnpinMsg]
  );

  // Dấu "đã xem": ai đã đọc tin CUỐI của tôi (readAt >= thời điểm tin).
  const seenBy = (() => {
    const lastMine = [...messages].reverse().find((m) => String(m.sender?._id) === myId && !m.pending && !m.failed);
    if (!lastMine) return [];
    const t = new Date(lastMine.createdAt).getTime();
    return (conv.members || [])
      .filter((mm) => String(mm.id) !== myId && (readMap[String(mm.id)] || 0) >= t)
      .map((mm) => mm.name);
  })();

  // Kéo giãn cửa sổ từ góc trên-trái (cửa sổ neo góc dưới-phải). Bỏ qua trên mobile (đang full màn).
  const startResize = useCallback((e) => {
    if (window.innerWidth <= 980) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = panelRef.current?.getBoundingClientRect();
    const startW = rect?.width || 372;
    const startH = rect?.height || 520;
    const startX = e.clientX;
    const startY = e.clientY;
    const onMove = (ev) => {
      const w = Math.min(720, Math.max(300, startW + (startX - ev.clientX)));
      const h = Math.min(Math.round(window.innerHeight * 0.92), Math.max(360, startH + (startY - ev.clientY)));
      setSize({ w, h });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      setSize((s) => { if (s) { try { localStorage.setItem('ft_chat_size', JSON.stringify(s)); } catch {} } return s; });
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  // Kích thước áp dụng (chỉ khi không thu nhỏ; mobile để CSS lo full màn).
  const panelStyle = size && !minimized ? { width: size.w, height: size.h } : undefined;

  return (
    <div ref={panelRef} style={panelStyle} className={'chatpanel chatpanel-conv' + (front ? ' front' : '') + (minimized ? ' min' : '')}>
      {!minimized && <div className="chatpanel-resize" onMouseDown={startResize} title="Kéo để thay đổi kích thước" />}
      {peek && (
        <div className="chat-peek-overlay" onClick={() => setPeek(null)}>
          <div className="chat-peek" onClick={(e) => e.stopPropagation()}>
            <div className="chat-peek-head">
              <b>{peek.title} · {peek.lines.length}</b>
              <button type="button" className="chat-members-x" onClick={() => setPeek(null)} title="Đóng">×</button>
            </div>
            <div className="chat-peek-list">
              {peek.lines.map((l, i) => (
                <div key={i} className="chat-peek-row">
                  <span className="chat-peek-icon">{l.icon}</span>
                  <span className="chat-peek-name">{l.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="chathead" onClick={() => minimized && toggleMinimize(false)}>
        <button className="chathead-btn chathead-back" onClick={(e) => { e.stopPropagation(); closeWindow(conv.id); }} title="Quay lại danh sách">‹</button>
        <span className={'chat-avatar sm chathead-ava' + (isGroup ? ' group' : '')} style={{ position: 'relative' }}>
          {isGroup ? '👥' : <AvaImg src={conv.other?.avatar} name={conv.name} />}
          {!isGroup && conv.other?.id && onlineIds?.has(String(conv.other.id)) && <span className="chat-online-dot" title="Đang online" />}
        </span>
        <div
          className="chathead-title"
          onClick={onTitleClick ? (e) => { e.stopPropagation(); onTitleClick(); } : undefined}
          style={onTitleClick ? { cursor: 'pointer' } : undefined}
          title={onTitleClick ? 'Bấm để mở/đóng bảng thông tin' : undefined}
        >
          {conv.name}
          {minimized && minUnread > 0 && <span className="chathead-unread" style={{ marginLeft: 8 }}>{minUnread > 99 ? '99+' : minUnread}</span>}
          {!minimized && isGroup && <span className="small" style={{ opacity: .85, fontWeight: 500 }}> · {(conv.members || []).length} thành viên</span>}
          {!minimized && !isGroup && conv.other?.id && onlineIds?.has(String(conv.other.id)) && <span className="small" style={{ color: 'var(--ok,#16a34a)', fontWeight: 500 }}> · Đang hoạt động</span>}
        </div>
        {!minimized && !observer && (
          <button className="chathead-btn" onClick={(e) => { e.stopPropagation(); doMute(); }}
            title={muted ? 'Đang tắt âm — bấm để bật lại thông báo' : 'Tắt âm thông báo hội thoại này'}>
            {muted ? '🔕' : '🔔'}
          </button>
        )}
        {isGroup && !minimized && (
          <button className="chathead-btn" onClick={(e) => { e.stopPropagation(); setShowMembers((v) => !v); }} title="Xem thành viên nhóm">
            👥
          </button>
        )}
        <button className="chathead-btn chathead-min" onClick={(e) => { e.stopPropagation(); toggleMinimize(); }} title={minimized ? 'Mở lại' : 'Thu nhỏ'}>
          {minimized ? '▢' : '—'}
        </button>
        <button className="chathead-btn" onClick={(e) => { e.stopPropagation(); if (isMobile()) setOpen(false); else closeWindow(conv.id); }} title="Đóng cửa sổ chat">
          ×
        </button>
      </div>
      {isGroup && showMembers && !minimized && (
        <div className="chat-members">
          <div className="chat-members-head">
            <b>Thành viên ({(conv.members || []).length})</b>
            <div className="row" style={{ gap: 6 }}>
              <button className="btn sm" disabled={memberBusy} onClick={renameGroupChat} title="Đổi tên nhóm">✎ Đổi tên</button>
              {canAddMember && (
                <button className="btn sm" onClick={openAddMember}>{showAddMember ? '✕ Đóng' : '＋ Thêm'}</button>
              )}
              <button className="chat-members-x" onClick={() => setShowMembers(false)} title="Đóng">×</button>
            </div>
          </div>

          {canAddMember && showAddMember && (() => {
            const memberIdSet = new Set((conv.members || []).map((m) => String(m.id)));
            const nq = addQ.trim().toLowerCase();
            const options = allUsers
              .filter((u) => !memberIdSet.has(String(u.id)))
              .filter((u) => !nq || String(u.name).toLowerCase().includes(nq))
              .slice(0, 30);
            return (
              <div className="chat-members-add">
                <input autoFocus value={addQ} onChange={(e) => setAddQ(e.target.value)} placeholder="🔍 Tìm nhân viên để thêm…" style={{ width: '100%' }} />
                <div className="chat-members-addlist">
                  {options.length ? options.map((u) => (
                    <div key={u.id} className="chat-members-row" style={{ cursor: memberBusy ? 'wait' : 'pointer' }} onClick={() => addMember(u.id)}>
                      <span className="chat-avatar sm"><AvaImg src={u.avatar} name={u.name} /></span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="chat-members-name">{u.name}</div>
                        <div className="small muted">{(u.departments || []).join(', ') || u.role}</div>
                      </div>
                      <span className="small" style={{ color: 'var(--accent2)' }}>Thêm</span>
                    </div>
                  )) : <div className="small muted" style={{ padding: '6px 12px' }}>Không còn ai để thêm.</div>}
                </div>
              </div>
            );
          })()}

          <div className="chat-members-list">
            {[...(conv.members || [])]
              .sort((a, b) => (onlineIds?.has(String(b.id)) ? 1 : 0) - (onlineIds?.has(String(a.id)) ? 1 : 0) || String(a.name).localeCompare(String(b.name), 'vi'))
              .map((mm) => {
                const on = onlineIds?.has(String(mm.id));
                const memberIsCreator = String(conv.createdBy || '') === String(mm.id);
                return (
                  <div key={mm.id} className="chat-members-row chat-members-row--click" onClick={() => openProfile(mm)} title="Xem thông tin nhân viên">
                    <span className="chat-avatar sm" style={{ position: 'relative' }}>
                      <AvaImg src={mm.avatar} name={mm.name} />
                      {on && <span className="chat-online-dot" title="Đang online" />}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="chat-members-name">
                        {mm.name}{String(mm.id) === myId ? ' (bạn)' : ''}
                        {memberIsCreator ? <span className="small muted"> · tạo nhóm</span> : mm.role === 'TP' ? <span className="small muted"> · TP</span> : mm.role === 'GD' ? <span className="small muted"> · GĐ</span> : null}
                      </div>
                      <div className="small" style={{ color: on ? 'var(--ok,#16a34a)' : 'var(--muted)' }}>{on ? 'Đang online' : 'Ngoại tuyến'}</div>
                    </div>
                    {canRemoveMember && !memberIsCreator && (
                      <button className="chat-members-remove" title={`Xoá ${mm.name} khỏi nhóm`} disabled={memberBusy} onClick={(e) => { e.stopPropagation(); removeMember(mm.id, mm.name); }}>✕</button>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}
      {profileUser && (
        <div className="chat-profile-overlay" onClick={() => setProfileUser(null)}>
          <div className="chat-profile-card" onClick={(e) => e.stopPropagation()}>
            <button className="chat-members-x" style={{ position: 'absolute', top: 8, right: 10 }} onClick={() => setProfileUser(null)} title="Đóng">×</button>
            <div className="chat-avatar" style={{ width: 56, height: 56, fontSize: 22, position: 'relative' }}>
              <AvaImg src={profileUser.avatar} name={profileUser.name} />
              {onlineIds?.has(String(profileUser.id)) && <span className="chat-online-dot" title="Đang online" />}
            </div>
            <div style={{ fontWeight: 700, fontSize: 16, marginTop: 8 }}>{profileUser.name}{String(profileUser.id) === myId ? ' (bạn)' : ''}</div>
            <div className="small muted">{ROLE_LABEL[profileUser.role] || 'Nhân viên'}</div>
            <div className="small" style={{ marginTop: 6 }}>
              <b>Phòng ban:</b> {(profileUser.departments || []).length ? profileUser.departments.join(', ') : '—'}
            </div>
            <div className="small" style={{ marginTop: 2, color: onlineIds?.has(String(profileUser.id)) ? 'var(--ok,#16a34a)' : 'var(--muted)' }}>
              {onlineIds?.has(String(profileUser.id)) ? '● Đang online' : '○ Ngoại tuyến'}
            </div>
            {String(profileUser.id) !== myId && (
              <button className="btn" style={{ marginTop: 14, width: '100%' }} onClick={chatWithProfile}>💬 Nhắn tin</button>
            )}
          </div>
        </div>
      )}
      {/* DẢI TIN GHIM (kiểu Zalo): tối đa 3, của CHUNG hội thoại. Mặc định thu gọn còn 1 dòng;
          bấm dòng ghim -> cuộn tới tin gốc (nếu tin còn trong khung); GIÁM SÁT chỉ xem, không bỏ ghim. */}
      {!minimized && pinnedMsgs.length > 0 && (
        <div className="chatpin-bar">
          {(pinsOpen ? pinnedMsgs : pinnedMsgs.slice(0, 1)).map((p) => (
            <div key={p.msgId} className="chatpin-row" onClick={() => scrollToMsg(p.msgId)} title="Bấm để cuộn tới tin gốc">
              <span className="chatpin-ico">📌</span>
              <span className="chatpin-text"><b>{p.sender}:</b> {p.text}</span>
              {!observer && (
                <button type="button" className="chatpin-x" title="Bỏ ghim tin này"
                  onClick={(e) => { e.stopPropagation(); doUnpinMsg(p.msgId); }}>×</button>
              )}
            </div>
          ))}
          {pinnedMsgs.length > 1 && (
            <button type="button" className="chatpin-more" onClick={() => setPinsOpen((v) => !v)}>
              {pinsOpen ? 'Thu gọn ▴' : `+${pinnedMsgs.length - 1} tin ghim khác ▾`}
            </button>
          )}
        </div>
      )}
      <div className="chatbody">
        <div
          className={'chatthread' + (dragOver ? ' dragover' : '')}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={(e) => {
            if (e.currentTarget === e.target) setDragOver(false);
          }}
          onDrop={onDrop}
        >
          {dragOver && <div className="chat-droplay">Thả tệp để gửi</div>}
          <div
            className="chatmsgs"
            ref={msgsRef}
            onScroll={() => { if (newBelow && isNearBottom()) setNewBelow(0); }}
            onClick={(e) => { if (e.target.classList.contains('chat-att-img')) setImgView(e.target.src); }}
          >
            {loadingMsgs ? <div className="chatempty">Đang tải…</div> : messageEls}
            {seenBy.length > 0 && (
              <div
                className="chatmsg-seen"
                title={isGroup ? 'Bấm để xem ai đã đọc' : seenBy.join(', ')}
                style={isGroup ? { cursor: 'pointer' } : undefined}
                onClick={isGroup ? () => setPeek({ title: 'Đã đọc', lines: seenBy.map((n) => ({ icon: '✓', name: n })) }) : undefined}
              >
                ✓ {isGroup ? `Đã xem bởi ${seenBy.length}` : 'Đã xem'}
              </div>
            )}
            {typingNames.length > 0 && (
              <div className="chat-typing">
                <span className="chat-typing-dots"><i /><i /><i /></span>
                <span className="small muted">{isGroup ? `${typingNames.join(', ')} đang gõ…` : 'đang gõ…'}</span>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Đang đọc tin cũ mà có tin mới: KHÔNG kéo tụt xuống — hiện mũi tên, bấm mới nhảy về tin mới nhất */}
          {newBelow > 0 && (
            <button type="button" className="chat-jump-latest" onClick={jumpToLatest}>
              ↓ {newBelow} tin nhắn mới
            </button>
          )}

          {atts.length > 0 && (
            <div className="chat-att-strip">
              {atts.map((a, i) => (
                <div className="chat-att-chip" key={i}>
                  {a.preview ? <img src={a.preview} alt="" /> : <span className="chat-att-doc">📎 {a.name}</span>}
                  <button type="button" className="chat-att-x" onClick={() => setAtts((prev) => prev.filter((_, j) => j !== i))}>
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {replyTo && (
            <div className="chat-reply-bar">
              <div className="chat-reply-info">
                <span className="chat-reply-label">↩ Trả lời {replyTo.senderName}</span>
                <span className="chat-reply-text">{replyTo.text || 'đã gửi tệp đính kèm'}</span>
              </div>
              <button type="button" className="chat-reply-x" onClick={() => setReplyTo(null)} title="Huỷ trả lời">×</button>
            </div>
          )}

          {imgView && (
            <div className="chat-lightbox" onClick={() => setImgView(null)}>
              <button type="button" className="chat-lightbox-x" onClick={() => setImgView(null)} title="Đóng ảnh">×</button>
              <img src={imgView} alt="" onClick={(e) => e.stopPropagation()} />
            </div>
          )}

          {observer ? (
            <div className="chatcompose chat-observer">
              <span>👁 Chế độ giám sát — chỉ xem, không thể nhắn hay tương tác</span>
              <button type="button" className="btn sm ghost" onClick={() => listMessages(conv.id, { limit: 40 }).then(setMessages).catch(() => {})}>
                🔄 Tải tin mới
              </button>
            </div>
          ) : (
          <form className="chatcompose" onSubmit={submit}>
            {suggestions.length > 0 && (
              <div className="chat-mention-pop">
                {suggestions.slice(0, 8).map((o) => (
                  <div key={o.id} className="chat-mention-item" onMouseDown={(e) => e.preventDefault()} onClick={() => pickMention(o)}>
                    <span className={'chat-avatar sm' + (o.id === 'all' ? ' group' : '')}>{o.id === 'all' ? '📣' : <AvaImg src={o.avatar} name={o.name} />}</span>
                    {o.id === 'all' ? <b>@Tất cả thành viên</b> : o.name}
                  </div>
                ))}
              </div>
            )}
            {showEmoji && <EmojiPicker onPick={insertEmoji} onSend={sendSticker} />}
            <button
              ref={emojiBtnRef}
              type="button"
              className={'chathead-btn chat-attbtn' + (showEmoji ? ' on' : '')}
              title="Sticker & emoji"
              onClick={() => setShowEmoji((v) => !v)}
            >
              😊
            </button>
            <button type="button" className="chathead-btn chat-attbtn" title="Đính kèm tệp" onClick={() => fileRef.current?.click()}>
              📎
            </button>
            <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => addFiles(e.target.files)} />
            {/* Ô nhập NHIỀU DÒNG: tự cao theo nội dung (tối đa ~5 dòng — xem effect chỉnh height).
                Enter = GỬI (trừ lúc đang gõ dấu tiếng Việt — isComposing) · Shift+Enter = xuống dòng. */}
            <textarea
              ref={inputRef}
              rows={1}
              value={text}
              onChange={onChange}
              onPaste={onPaste}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing) {
                  e.preventDefault();
                  e.currentTarget.form?.requestSubmit();
                }
              }}
              placeholder={isGroup ? 'Nhập tin nhắn… (Enter gửi · Shift+Enter xuống dòng · @ để tag)' : 'Nhập tin nhắn… (Enter gửi · Shift+Enter xuống dòng)'}
              autoFocus={front}
            />
            <button type="submit" className="btn sm" disabled={sending || (!text.trim() && !atts.length)}>
              {sending ? '…' : 'Gửi'}
            </button>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Settings() {
  const { soundOn, setSoundOn, notifOn, setNotifOn } = useChat();
  const toast = useToast();
  const [perm, setPerm] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
  const [testing, setTesting] = useState(false);

  async function askPermission() {
    if (typeof Notification === 'undefined') return;
    const p = await Notification.requestPermission();
    setPerm(p);
    if (p === 'granted') {
      const { registerPush } = await import('../push');
      await registerPush();
      toast('Đã bật thông báo Chrome');
    }
  }

  async function sendTest() {
    setTesting(true);
    try {
      const { registerPush } = await import('../push');
      await registerPush(); // đảm bảo đã đăng ký subscription
      const { testPush } = await import('../api/chat');
      const r = await testPush();
      if (r.subs === 0) toast('Chưa có thiết bị nào đăng ký nhận. Hãy bật thông báo Chrome trước.', 'warn');
      else if (r.sent === 0) toast('Gửi thất bại — kiểm tra quyền thông báo của trình duyệt.', 'danger');
      else toast('Đã gửi thông báo thử. Thử đóng tab rồi gửi lại để kiểm tra.');
    } catch {
      toast('Không gửi được thông báo thử', 'danger');
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="chatnew">
      <label className="chat-set-row">
        <span>🔔 Thông báo trên Chrome</span>
        <input type="checkbox" checked={notifOn} onChange={(e) => setNotifOn(e.target.checked)} />
      </label>
      <label className="chat-set-row">
        <span>🔊 Âm thanh báo tin nhắn</span>
        <input type="checkbox" checked={soundOn} onChange={(e) => setSoundOn(e.target.checked)} />
      </label>

      <div className="chat-set-perm">
        Quyền thông báo trình duyệt:{' '}
        <b style={{ color: perm === 'granted' ? 'var(--accent2)' : 'var(--warn)' }}>
          {perm === 'granted' ? 'Đã bật' : perm === 'denied' ? 'Bị chặn' : 'Chưa bật'}
        </b>
        {perm !== 'granted' && (
          <button className="btn sm" style={{ marginTop: 10, width: '100%' }} onClick={askPermission}>
            Bật thông báo Chrome
          </button>
        )}
        {perm === 'denied' && (
          <div className="small muted" style={{ marginTop: 8 }}>
            Bạn đã chặn thông báo — hãy mở khóa ở biểu tượng 🔒 trên thanh địa chỉ Chrome rồi tải lại trang.
          </div>
        )}
      </div>
      <button className="btn ghost" style={{ marginTop: 14, width: '100%' }} disabled={testing} onClick={sendTest}>
        {testing ? 'Đang gửi…' : '🔔 Gửi thông báo thử'}
      </button>
      <div className="small muted" style={{ marginTop: 12 }}>
        Âm thanh phát khi có tin nhắn mới mà bạn chưa mở đúng cuộc trò chuyện. Thông báo Chrome còn báo cả khi đã đóng tab —
        nhưng Chrome vẫn phải đang chạy (không thoát hẳn) và bạn đã bấm "Bật thông báo Chrome".
      </div>
    </div>
  );
}

function NewChat() {
  const { startDirect, startGroup } = useChat();
  const [tab, setTab] = useState('direct'); // 'direct' | 'group'
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [groupName, setGroupName] = useState('');
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    chatUsers().then(setUsers).catch(() => {});
  }, []);

  const filtered = users.filter((u) => u.name.toLowerCase().includes(q.toLowerCase()));

  async function doDirect(u) {
    setBusy(true);
    try {
      await startDirect(u.id);
    } finally {
      setBusy(false);
    }
  }
  function togglePick(id) {
    setPicked((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  }
  async function doGroup() {
    if (!groupName.trim() || picked.size < 1) return;
    setBusy(true);
    try {
      await startGroup({ name: groupName.trim(), memberIds: [...picked] });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="chatnew">
      <div className="seg" style={{ margin: '4px 0 10px' }}>
        <button className={tab === 'direct' ? 'active' : ''} onClick={() => setTab('direct')}>
          Chat cá nhân
        </button>
        <button className={tab === 'group' ? 'active' : ''} onClick={() => setTab('group')}>
          Tạo nhóm
        </button>
      </div>

      {tab === 'group' && (
        <input style={{ marginBottom: 8 }} value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Tên nhóm (vd: Phòng kinh doanh)" />
      )}
      <input style={{ marginBottom: 8 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 Tìm nhân viên…" />

      <div className="chatuserlist">
        {filtered.map((u) => (
          <div
            key={u.id}
            className="chatuser-row"
            onClick={() => (tab === 'direct' ? doDirect(u) : togglePick(u.id))}
          >
            {tab === 'group' && <input type="checkbox" readOnly checked={picked.has(u.id)} />}
            <div className="chat-avatar sm"><AvaImg src={u.avatar} name={u.name} /></div>
            <div className="chatuser-main">
              <div className="chatuser-name">{u.name}</div>
              <div className="chatuser-sub">{(u.departments || []).join(', ') || u.role}</div>
            </div>
          </div>
        ))}
        {!filtered.length && <div className="chatempty">Không tìm thấy nhân viên.</div>}
      </div>

      {tab === 'group' && (
        <button className="btn" style={{ marginTop: 10, width: '100%' }} disabled={busy || !groupName.trim() || picked.size < 1} onClick={doGroup}>
          Tạo nhóm ({picked.size})
        </button>
      )}
    </div>
  );
}
